import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, List, Button, Spin, Alert, Image, Typography, Row, Col, Tag, Avatar, Modal, Checkbox, Input, Form, message as antdMessage, Select } from 'antd';
import { getEmployee, sendEmployeeDocuments, imagepath } from '../api';
import { FileOutlined, UserOutlined, FilePdfOutlined, MailOutlined, EditOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const labelStyle = { color: '#888', fontWeight: 500, marginBottom: 2, display: 'block' };
const valueStyle = { fontSize: 16, marginBottom: 12, display: 'block' };
const cardStyle = { background: '#fafcff', borderRadius: 10, boxShadow: '0 2px 8px #f0f1f2', marginBottom: 32 };

function isImage(fileName) {
  return /\.(jpg|jpeg|png)$/i.test(fileName);
}
function isPdf(fileName) {
  return /\.(pdf)$/i.test(fileName);
}
// Color code logic (same as employee listing)
function getStatusColor(status) {
  if (status === 'active') return '#f6ffed'; // light green
  if (status === 'inactive') return '#fff1f0'; // light red
  return '#f5f5f5'; // neutral
}
function getAvatarColor(status) {
  if (status === 'active') return '#52c41a'; // green
  if (status === 'inactive') return '#f5222d'; // red
  return '#bfbfbf'; // gray
}
function getInitials(name) {
  if (!name) return '';
  const parts = name.split(' ');
  return (parts[0][0] || '') + (parts[1]?.[0] || '');
}

// Update getColor utility to match Employees.js exactly
function getColor(name) {
  const colors = ['#1890ff', '#f5222d', '#faad14', '#52c41a', '#722ed1', '#13c2c2', '#eb2f96', '#b37feb'];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

const EmployeeView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [emailForm] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [docFilter, setDocFilter] = useState('All');

  useEffect(() => {
    setLoading(true);
    getEmployee(id).then(res => {
      if (res.success) {
        setEmployee(res.data);
      } else {
        setError(res.message || 'Failed to load employee');
      }
      setLoading(false);
    });
  }, [id]);

  if (loading) return <div style={{ textAlign: 'center', marginTop: 100 }}><Spin size="large" /></div>;
  if (error) return <Alert type="error" message={error} style={{ margin: 40 }} />;
  if (!employee) return null;

  const statusColor = getStatusColor(employee.status);
  const avatarColor = getAvatarColor(employee.status);
  const initials = getInitials(employee.name);

  // Modal Handlers
  const openShareModal = () => {
    setSelectedDocs(employee.documents?.map(doc => doc.id) || []); // default: all selected
    setShareModalOpen(true);
  };
  const closeShareModal = () => {
    setShareModalOpen(false);
    emailForm.resetFields();
  };
  const handleDocCheckbox = (checked, docId) => {
    setSelectedDocs(checked
      ? [...selectedDocs, docId]
      : selectedDocs.filter(id => id !== docId)
    );
  };
  const handleSend = async (values) => {
    if (!selectedDocs.length) {
      antdMessage.error('Please select at least one document to share.');
      return;
    }
    setSending(true);
    try {
      const res = await sendEmployeeDocuments(employee.id, {
        document_ids: selectedDocs,
        to: values.to,
        cc: values.cc,
        bcc: values.bcc,
        message: values.message,
      });
      if (res.success) {
        antdMessage.success('Email sent successfully!');
        setShareModalOpen(false);
        emailForm.resetFields();
      } else {
        antdMessage.error(res.message || 'Failed to send email.');
      }
    } catch (e) {
      antdMessage.error('Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <Button onClick={() => navigate(-1)}>Back</Button>
        <Button
          type="primary"
          icon={<EditOutlined />}
          style={{ background: '#1890ff' }}
          onClick={() => navigate(`/employees/${employee.id}/edit`)}
        >
          Edit Employee
        </Button>
      </div>
      <Row gutter={32} style={{ marginBottom: 32 }} align="stretch">
        {/* Compact Employee Card (left) */}
        <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
          <Card
            size="small"
            style={{ display: 'flex', alignItems: 'center', marginBottom: 0, padding: 16, borderRadius: 14, boxShadow: '0 6px 32px rgba(0,0,0,0.14)', background: statusColor, border: '1px solid #e0e0e0' }}
            bordered={false}
          >
            <Avatar
              src={employee.profile_image ? `${imagepath}${employee.profile_image}` : undefined}
              size={64}
              style={{ backgroundColor: employee.profile_image ? undefined : getColor(employee.name), color: '#fff', fontSize: 28, marginRight: 20, objectFit: 'cover' }}
              icon={!employee.profile_image ? <UserOutlined /> : undefined}
            >
              {!employee.profile_image && initials}
            </Avatar>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 20 }}>{employee.name}</div>
              <div style={{ color: '#888', fontSize: 15 }}>{employee.emp_code}</div>
              <div style={{ color: '#888', fontSize: 15 }}>{employee.department?.name}</div>
              <Tag color={employee.status === 'active' ? 'green' : 'red'} style={{ marginTop: 4 }}>{employee.status?.toUpperCase()}</Tag>
            </div>
            <div style={{ minWidth: 180 }}>
              <div style={{ color: '#888', fontSize: 15 }}><b>Phone (UAE):</b> {employee.phone}</div>
              <div style={{ color: '#888', fontSize: 15 }}><b>Phone (Native):</b> {employee.native_phone}</div>
              <div style={{ color: '#888', fontSize: 15 }}><b>Email:</b> {employee.email}</div>
            </div>
          </Card>
        </Col>
        {/* Personal Info Card (right half) */}
        <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
          <Card title="Personal Info" style={{ ...cardStyle, height: '100%', marginBottom: 0, border: '1px solid #e0e0e0', boxShadow: '0 6px 32px rgba(0,0,0,0.14)', borderRadius: 14 }} bordered={false}>

            <Text style={labelStyle}>Date of Birth</Text>
            <Text style={valueStyle}>{employee.dob}</Text>
            <Text style={labelStyle}>Address</Text>
            <Text style={valueStyle}>{employee.address}</Text>
            <Text style={labelStyle}>Country</Text>
            <Text style={valueStyle}>{employee.country}</Text>
          </Card>
        </Col>
      </Row>
      {/* Job Info and Documents Cards in the same row */}
      <Row gutter={32} style={{ marginBottom: 32, display: 'flex', alignItems: 'stretch' }}>
        {/* Job Info Card (left half, two columns) */}
        <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', marginBottom: 0, height: '100%' }}>
          <Card title="Job Info" style={{ ...cardStyle, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 400, boxShadow: '0 6px 32px rgba(0,0,0,0.14)', border: '1px solid #e0e0e0', borderRadius: 14 }} bordered={false}>
            <Row gutter={16} style={{ height: '100%' }}>
              <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column' }}>
                <Text style={labelStyle}>Companay</Text>
                <Text style={valueStyle}>{employee.department?.name}</Text>
                <Text style={labelStyle}>Joining Date</Text>
                <Text style={valueStyle}>{employee.joining_date}</Text>
                <Text style={labelStyle}>Salary Type</Text>
                <Text style={valueStyle}>{employee.salary_type}</Text>
              </Col>
              <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column' }}>
                <Text style={labelStyle}>Workmen Insurance Issue Date</Text>
                <Text style={valueStyle}>{employee.workmen_insurance_issue_date || 'N/A'}</Text>
                <Text style={labelStyle}>Workmen Insurance Expiry Date</Text>
                <Text style={valueStyle}>{employee.workmen_insurance_expiry_date || 'N/A'}</Text>
                <Text style={labelStyle}>Base Salary</Text>
                <Text style={valueStyle}>{employee.base_salary}</Text>
                <Text style={labelStyle}>Hourly Rate</Text>
                <Text style={valueStyle}>{employee.hourly_rate}</Text>
                <Text style={labelStyle}>Overtime Rate</Text>
                <Text style={valueStyle}>{employee.over_time_rate}</Text>
              </Col>
            </Row>
          </Card>
        </Col>
        {/* Documents Card (right half) */}
        <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', marginBottom: 0, height: '100%' }}>
          <Card title="Documents" style={{ ...cardStyle, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 400, boxShadow: '0 6px 32px rgba(0,0,0,0.14)', border: '1px solid #e0e0e0', borderRadius: 14 }} bordered={false}
            extra={
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Select
                  value={docFilter}
                  onChange={setDocFilter}
                  style={{ width: 140 }}
                  options={[
                    { value: 'All', label: 'All Types' },
                    ...(employee ? Array.from(new Set((employee.documents || []).map(d => d.document_type))).map(type => ({ value: type, label: type.replace(/_/g, ' ').toUpperCase() })) : [])
                  ]}
                />
                <Button icon={<MailOutlined />} onClick={openShareModal}>Share</Button>
              </div>
            }
          >
            <List
              grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 2 }}
              dataSource={(employee.documents || []).filter(doc => docFilter === 'All' || doc.document_type === docFilter)}
              locale={{ emptyText: 'No documents uploaded' }}
              renderItem={doc => {
                let isExpired = false;
                if (doc.expiry_date) {
                  const expDate = new Date(doc.expiry_date);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  if (expDate < today) isExpired = true;
                }
                return (
                <List.Item>
                  <Card size="small" style={{ minHeight: 180, textAlign: 'center', background: isExpired ? '#fff1f0' : '#f5f5f5', border: isExpired ? '1px solid #ffa39e' : undefined }}>
                    <div style={{ fontWeight: 500, marginBottom: 8, color: isExpired ? '#cf1322' : undefined }}>
                      {doc.document_type.replace(/_/g, ' ').toUpperCase()}
                      {isExpired && <Tag color="red" style={{ marginLeft: 8 }}>Expired</Tag>}
                    </div>
                    {doc.issue_date && <div style={{ color: '#888', marginBottom: 4 }}>Issued: {doc.issue_date}</div>}
                    <div style={{ color: isExpired ? '#cf1322' : '#888', marginBottom: 4, fontWeight: isExpired ? 'bold' : 'normal' }}>Expiry: {doc.expiry_date}</div>
                    {doc.original_filename && <div style={{ color: '#555', marginBottom: 4 }}>File: {doc.original_filename}</div>}
                    {doc.document_file && isImage(doc.document_file) ? (
                      <Image
                        src={`${imagepath + doc.document_file}`}
                        width={60}
                        height={60}
                        style={{ objectFit: 'cover', borderRadius: 6, marginBottom: 8, boxShadow: '0 1px 4px #ddd' }}
                        alt={doc.original_filename}
                        preview={true}
                      />
                    ) : doc.document_file && isPdf(doc.document_file) ? (
                      <FilePdfOutlined style={{ fontSize: 40, color: '#cf1322', marginBottom: 8 }} />
                    ) : doc.document_file ? (
                      <FileOutlined style={{ fontSize: 40, color: '#888', marginBottom: 8 }} />
                    ) : null}
                    {doc.document_file && (
                      <div>
                        <a href={`${imagepath + doc.document_file}`} target="_blank" rel="noopener noreferrer">View/Download</a>
                      </div>
                    )}
                  </Card>
                </List.Item>
                );
              }}
            />
          </Card>
        </Col>
      </Row>
      <Row gutter={32} style={{ marginBottom: 32 }}>
        <Col span={24}>
          <Card title="Leave History" style={{ ...cardStyle, boxShadow: '0 6px 32px rgba(0,0,0,0.14)', border: '1px solid #e0e0e0', borderRadius: 14 }} bordered={false}>
            <List
              dataSource={employee.leaves || []}
              locale={{ emptyText: 'No leave history recorded' }}
              renderItem={leave => (
                <List.Item>
                  <List.Item.Meta
                    title={<Text strong>{leave.leave_type}</Text>}
                    description={
                      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Text><b>From:</b> {leave.start_date}</Text>
                        <Text><b>To:</b> {leave.end_date}</Text>
                        {leave.actual_return_date && (
                          <>
                            <Text><b>Actual Return:</b> {leave.actual_return_date}</Text>
                            {(() => {
                              const end = new Date(leave.end_date);
                              const actual = new Date(leave.actual_return_date);
                              const expected = new Date(end);
                              expected.setDate(expected.getDate() + 1);
                              expected.setHours(0, 0, 0, 0);
                              actual.setHours(0, 0, 0, 0);
                              const diffTime = actual - expected;
                              const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                              if (diffDays > 0) return <Tag color="red">{diffDays} Day(s) Late</Tag>;
                              if (diffDays < 0) return <Tag color="green">{Math.abs(diffDays)} Day(s) Early</Tag>;
                              return <Tag color="blue">On Time</Tag>;
                            })()}
                          </>
                        )}
                        {leave.reason && <Text><b>Reason:</b> {leave.reason}</Text>}
                        {leave.leave_application_form && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <FileOutlined style={{ color: '#1890ff' }} />
                            <a href={`${imagepath}${leave.leave_application_form}`} target="_blank" rel="noopener noreferrer">
                              View Form
                            </a>
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
      {/* Share Documents Modal */}
      <Modal
        title="Share Documents via Email"
        open={shareModalOpen}
        onCancel={closeShareModal}
        footer={null}
        width={600}
      >
        <Form layout="vertical" form={emailForm} onFinish={handleSend}>
          <Form.Item label="Select Documents to Share" required>
            <Checkbox.Group
              style={{ width: '100%' }}
              value={selectedDocs}
              onChange={setSelectedDocs}
            >
              <List
                dataSource={employee.documents || []}
                renderItem={doc => (
                  <List.Item style={{ padding: 4 }}>
                    <Checkbox value={doc.id} style={{ marginRight: 8 }}>
                      {doc.document_file && isImage(doc.document_file) ? (
                        <Image
                          src={`${doc.document_file}`}
                          width={32}
                          height={32}
                          style={{ objectFit: 'cover', borderRadius: 4, marginRight: 8 }}
                          alt={doc.original_filename}
                          preview={false}
                        />
                      ) : doc.document_file && isPdf(doc.document_file) ? (
                        <FilePdfOutlined style={{ fontSize: 24, color: '#cf1322', marginRight: 8 }} />
                      ) : doc.document_file ? (
                        <FileOutlined style={{ fontSize: 24, color: '#888', marginRight: 8 }} />
                      ) : null}
                      {doc.original_filename || doc.document_type}
                    </Checkbox>
                  </List.Item>
                )}
              />
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label="To" name="to" rules={[{ required: true, message: 'Recipient email is required' }]}> <Input placeholder="Recipient email(s), comma separated" /> </Form.Item>
          <Form.Item label="CC" name="cc"> <Input placeholder="CC email(s), comma separated" /> </Form.Item>
          <Form.Item label="BCC" name="bcc"> <Input placeholder="BCC email(s), comma separated" /> </Form.Item>
          <Form.Item label="Message" name="message"> <TextArea rows={3} placeholder="Add a message (optional)" /> </Form.Item>
          <div style={{ textAlign: 'right' }}>
            <Button onClick={closeShareModal} style={{ marginRight: 8 }} disabled={sending}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={sending}>Send</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeeView; 